import {
  CheckoutFilterOption,
  ComedianFilterOption,
  DateFilterOption,
  FilterState,
} from "./ShowsNearbyFilters";
import { Dialog, DialogContent } from "@/shadcn/ui/dialog";
import { Drawer, DrawerContent } from "@/shadcn/ui/drawer";

import { FilterButton } from "./FilterButton";
import { useMediaQuery } from "@/components/common/hooks/useMediaQuery";

interface ShowsFiltersModalOrDrawerProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  isPending: boolean;
  filterState: FilterState;
  resetFilters: () => void;
  optimisticState: FilterState;
  handleDateFilter: (filter: DateFilterOption) => void;
  handleComedianFilter: (filter: ComedianFilterOption) => void;
  handleCheckoutFilter: (filter: CheckoutFilterOption) => void;
}

export function ShowsFiltersModalOrDrawer({
  isOpen,
  setIsOpen,
  isPending,
  filterState,
  resetFilters,
  optimisticState,
  handleDateFilter,
  handleComedianFilter,
  handleCheckoutFilter,
}: ShowsFiltersModalOrDrawerProps) {
  const isDesktop = useMediaQuery("(min-width: 768px)");

  if (isDesktop) {
    return (
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="sm:max-w-[425px] md:min-w-[800px] gap-6 rounded-2xl p-0">
          <ShowsFiltersContent
            filterState={filterState}
            isPending={isPending}
            optimisticState={optimisticState}
            handleDateFilter={handleDateFilter}
            handleComedianFilter={handleComedianFilter}
            handleCheckoutFilter={handleCheckoutFilter}
            resetFilters={resetFilters}
            setIsOpen={setIsOpen}
          />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Drawer open={isOpen} onOpenChange={setIsOpen}>
      <DrawerContent className="p-0">
        <ShowsFiltersContent
          filterState={filterState}
          isPending={isPending}
          optimisticState={optimisticState}
          handleDateFilter={handleDateFilter}
          handleComedianFilter={handleComedianFilter}
          handleCheckoutFilter={handleCheckoutFilter}
          resetFilters={resetFilters}
          setIsOpen={setIsOpen}
        />
      </DrawerContent>
    </Drawer>
  );
}

function ShowsFiltersContent({
  filterState,
  isPending,
  optimisticState,
  handleDateFilter,
  handleComedianFilter,
  handleCheckoutFilter,
  resetFilters,
  setIsOpen,
}: {
  filterState: FilterState;
  isPending: boolean;
  optimisticState: FilterState;
  handleDateFilter: (filter: DateFilterOption) => void;
  handleComedianFilter: (filter: ComedianFilterOption) => void;
  handleCheckoutFilter: (filter: CheckoutFilterOption) => void;
  resetFilters: () => void;
  setIsOpen: (open: boolean) => void;
}) {
  // TODO: if the user is NOT logged in, we should show a sign in modal
  // assume the user is logged in for now

  return (
    <>
      <main className="flex flex-col gap-6 px-5 pt-4 pb-8 md:p-6">
        <section className="flex flex-col gap-3">
          <h2 className="text-[16px] font-bold">Comedians</h2>
          <div className="flex flex-row gap-2 flex-wrap">
            <FilterButton
              label="All Comedians"
              isSelected={optimisticState.comedianFilter === "all"}
              isPending={isPending}
              onClick={() => handleComedianFilter("all")}
            />
            <FilterButton
              label="My Comedians"
              isSelected={optimisticState.comedianFilter === "myComedians"}
              isPending={isPending}
              onClick={() => handleComedianFilter("myComedians")}
            />
            <FilterButton
              label="Other Comedians"
              isSelected={optimisticState.comedianFilter === "otherComedians"}
              isPending={isPending}
              onClick={() => handleComedianFilter("otherComedians")}
            />
          </div>
        </section>
        <section className="flex flex-col gap-3">
          <h2 className="text-[16px] font-bold">Dates</h2>
          <div className="flex flex-row gap-2 flex-wrap">
            <FilterButton
              label="All dates"
              isSelected={optimisticState.dateFilter === "all"}
              isPending={isPending}
              onClick={() => handleDateFilter("all")}
              // ensure all buttons in this row are the same width
            />
            <FilterButton
              label="This Weekend"
              isSelected={optimisticState.dateFilter === "thisWeekend"}
              isPending={isPending}
              onClick={() => handleDateFilter("thisWeekend")}
            />
            <FilterButton
              label="This Week"
              isSelected={optimisticState.dateFilter === "thisWeek"}
              isPending={isPending}
              onClick={() => handleDateFilter("thisWeek")}
            />
            <FilterButton
              label="This Month"
              isSelected={optimisticState.dateFilter === "thisMonth"}
              isPending={isPending}
              onClick={() => handleDateFilter("thisMonth")}
            />
          </div>
        </section>
        <section className="flex flex-col gap-3">
          <h2 className="text-[16px] font-bold">Checkout type</h2>
          <div className="flex flex-row gap-2 flex-wrap">
            <FilterButton
              label="Any"
              isSelected={optimisticState.checkoutFilter === "all"}
              isPending={isPending}
              onClick={() => handleCheckoutFilter("all")}
              className="px-6"
            />
            <FilterButton
              label="Fast Checkout"
              isSelected={optimisticState.checkoutFilter === "punchupVenues"}
              isPending={isPending}
              onClick={() => handleCheckoutFilter("punchupVenues")}
            />
            <FilterButton
              label="External Checkout"
              isSelected={optimisticState.checkoutFilter === "externalCheckout"}
              isPending={isPending}
              onClick={() => handleCheckoutFilter("externalCheckout")}
            />
          </div>
        </section>
      </main>
      <footer className="flex flex-col-reverse md:flex-row gap-3 md:gap-2 flex-wrap justify-end px-8 py-4 border-t border-gray-200">
        <button
          type="button"
          onClick={resetFilters}
          className="font-medium rounded-md bg-transparent px-8 py-2 text-primary-blue"
        >
          Clear all
        </button>
        <button
          type="button"
          onClick={() => setIsOpen(false)}
          className="font-medium rounded-md bg-primary px-16 py-3 md:py-2 text-white"
        >
          Save
        </button>
      </footer>
    </>
  );
}
